import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'

interface FailureDetailHeaderProps {
  failure: {
    id: string
    title: string
    tag_name: string | null
    tag_names: string[]
    short_id: string
  }
  onDelete: () => void
  onSaveShortId: (shortId: string) => Promise<void>
  onSaveTags?: (tagNames: string[]) => Promise<void>
}

function FailureDetailHeader({ failure, onDelete, onSaveShortId, onSaveTags }: FailureDetailHeaderProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingShortId, setEditingShortId] = useState(false)
  const [shortIdInput, setShortIdInput] = useState('')
  const [editingTags, setEditingTags] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [pendingTags, setPendingTags] = useState<string[]>([])
  const [savingTags, setSavingTags] = useState(false)

  const handleSaveShortId = useCallback(async () => {
    await onSaveShortId(shortIdInput)
    setEditingShortId(false)
  }, [onSaveShortId, shortIdInput])

  const startEditingTags = useCallback(() => {
    setPendingTags(failure.tag_names || [])
    setTagInput('')
    setEditingTags(true)
  }, [failure.tag_names])

  const addTag = useCallback(() => {
    const trimmed = tagInput.trim()
    if (trimmed && !pendingTags.includes(trimmed)) {
      setPendingTags([...pendingTags, trimmed])
    }
    setTagInput('')
  }, [tagInput, pendingTags])

  const removeTag = useCallback((name: string) => {
    setPendingTags(pendingTags.filter(t => t !== name))
  }, [pendingTags])

  const handleSaveTags = useCallback(async () => {
    if (!onSaveTags) return
    setSavingTags(true)
    try {
      await onSaveTags(pendingTags)
      setEditingTags(false)
    } finally {
      setSavingTags(false)
    }
  }, [onSaveTags, pendingTags])

  return (
    <header data-testid="failure-detail-header" className="failure-detail-header">
      <div className="failure-detail-header-left">
        <Link data-testid="failure-detail-back" to="/failures" className="failure-detail-back">&larr; Back to Failures</Link>
        <h1 data-testid="failure-detail-title" className="failure-detail-title">{failure.title}</h1>
        <div className="failure-detail-ids">
          {editingShortId ? (
            <span className="short-id-edit" role="group" aria-label="Edit short ID">
              <label htmlFor="short-id-input" className="sr-only">Short ID</label>
              <input
                id="short-id-input"
                data-testid="short-id-input"
                className="short-id-input"
                value={shortIdInput}
                onChange={(e) => setShortIdInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveShortId(); if (e.key === 'Escape') setEditingShortId(false); }}
                aria-required="true"
                autoFocus
              />
              <button data-testid="short-id-save" className="short-id-save-btn" onClick={handleSaveShortId}>Save</button>
              <button className="short-id-cancel-btn" onClick={() => setEditingShortId(false)}>Cancel</button>
            </span>
          ) : (
            <button
              data-testid="failure-short-id"
              className="failure-detail-short-id"
              onClick={() => { setShortIdInput(failure.short_id || ''); setEditingShortId(true); }}
              aria-label={`Edit short ID: ${failure.short_id || 'No ID'}`}
            >
              {failure.short_id || 'No ID'}
            </button>
          )}
          <code className="failure-detail-id">{failure.id}</code>
        </div>
        {editingTags ? (
          <div data-testid="failure-detail-tags-editor" className="failure-detail-tags">
            {pendingTags.map((name) => (
              <span key={name} className="failure-detail-tag editable" data-testid="failure-detail-tag-editable">
                {name}
                <button
                  type="button"
                  className="failure-detail-tag-remove"
                  onClick={() => removeTag(name)}
                  aria-label={`Remove tag ${name}`}
                >
                  x
                </button>
              </span>
            ))}
            <input
              data-testid="failure-tag-add-input"
              className="failure-detail-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                if (e.key === 'Escape') setEditingTags(false)
              }}
              placeholder="Add tag..."
              autoFocus
            />
            <button
              data-testid="failure-tags-save"
              className="short-id-save-btn"
              onClick={handleSaveTags}
              disabled={savingTags}
            >
              {savingTags ? 'Saving...' : 'Save'}
            </button>
            <button className="short-id-cancel-btn" onClick={() => setEditingTags(false)}>Cancel</button>
          </div>
        ) : (
          <div data-testid="failure-detail-tags" className="failure-detail-tags">
            {failure.tag_names && failure.tag_names.length > 0 ? (
              failure.tag_names.map((name) => (
                <span key={name} data-testid="failure-detail-tag" className="failure-detail-tag">{name}</span>
              ))
            ) : (
              <span className="failure-detail-no-tags">No tags</span>
            )}
            {onSaveTags && (
              <button
                data-testid="failure-edit-tags-btn"
                className="failure-detail-edit-tags-btn"
                onClick={startEditingTags}
                title="Edit tags"
              >
                Edit Tags
              </button>
            )}
          </div>
        )}
      </div>
      <div className="failure-detail-header-actions">
        {showDeleteConfirm ? (
          <div data-testid="failure-detail-delete-confirm" className="delete-confirm" role="alert">
            <span className="delete-confirm-text">Delete?</span>
            <button data-testid="failure-detail-confirm-delete" className="delete-confirm-yes" onClick={onDelete}>Yes</button>
            <button data-testid="failure-detail-cancel-delete" className="delete-confirm-no" onClick={() => setShowDeleteConfirm(false)}>No</button>
          </div>
        ) : (
          <button data-testid="failure-detail-delete" className="failure-detail-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
            Delete Failure
          </button>
        )}
      </div>
    </header>
  )
}

export default FailureDetailHeader
